<template>
  <b-card>
    <template v-if="!company">
      <error-block v-if="fatalErr" :err="fatalErr" full-page/>
      <no-ssr v-else><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Custom Fields</h3>
      <p>You can add custom fields to your platform, and optionally enforce users fill them on login or signup</p>

      <!-- Custom field groups -->
      <div v-for="group in allowedSteps" :key="group.name" class="settings-form-block mt-4">
        <div class="d-flex flex-row align-items-start pb-2">
          <div class="d-flex flex-column flex-fill">
            <span style="font-size: 18px">
              {{ group.displayName }}
            </span>
            <!-- slot name="description"><p>{{ description }}</p></slot -->
          </div>
          <b-button @click="() => visible[group.name] = !visible[group.name]" size="sm" variant="outline">
            <template v-if="visible[group.name]">Close</template>
            <template v-else>Expand</template>
          </b-button>
        </div>
        <transition name="fade">
          <div v-if="visible[group.name]">
            <div
              class="my-3"
              v-if="!company.customFields.filter(el => el.group === group.name).length && !newFields.length"
            ><p class="text-center text-muted">No custom fields added yet.</p></div>
            <template v-for="(field, i) in company.customFields.filter(el => el.group === group.name)">
              <custom-field-block
                :key="`block-${field.key}`"
                :value="field"
                @input="value => company.customFields[i] = value"
                :departments="company.departments"
                @save="value => updateField(i, value)"
                @remove="() => removeField(field)"
                :groupKey="group.name"
                :groupTypes="group.types || []"
              />
              <hr :key="`hr-${field.key}`"/>
            </template>
            <template v-for="(field, i) in newFields">
              <custom-field-block
                :key="`block-new-${field.key}`"
                new-field
                :value="field"
                @input="value => newFields[i] = value"
                :departments="company.departments"
                @save="value => createField(i, value)"
                @remove="() => removeNewField(i)"
                :groupKey="field.groupKey"
                :groupTypes="group.types || []"
              />
              <hr :key="`hr-new-${field.key}`"/>
            </template>

            <div v-if="group.types && group.types.length" class="d-flex flex-row justify-content-end mb-3">
              <b-btn id="add-new-field" variant="outline-primary" @click="addNewField(group.name)">Add New</b-btn>
            </div>
          </div>
        </transition>
      </div>
    </template>
  </b-card>
</template>

<script>
import gql from 'graphql-tag';
import ErrorBlock from '@/components/misc/ErrorBlock';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import CustomFieldBlock from '@/components/settings/CustomFieldBlock';
import { onboardingSteps } from '@/views/onboarding/index';

const CustomFieldFragment = gql`
  fragment CustomFieldDetails on CompanyCustomField {
    key
    type
    name
    description
    group
    departments {
      id
    }
    options
    required
    created
    updated
  }
`;
const ReadCustomFieldsQuery = gql`
  ${CustomFieldFragment}

  query ReadCompanyCustomFields {
    company {
      id

      siteBranding: branding {
        onboarding {
          name
        }
      }

      customFields(where: { status: ACTIVE }) {
        ...CustomFieldDetails
      }

      departments(where: { status: ACTIVE }) {
        id
        name
        icon
      }
    }
  }
`;
const CreateCustomFieldMutation = gql`
  ${CustomFieldFragment}

  mutation CreateCompanyCustomField($create: CreateCompanyCustomFieldInput!) {
    createCompanyCustomField(create: $create) {
      ...CustomFieldDetails
    }
  }
`;
const UpdateCustomFieldMutation = gql`
  ${CustomFieldFragment}

  mutation UpdateCompanyCustomField($update: UpdateCompanyCustomFieldInput!, $key: String!) {
    updateCompanyCustomField(update: $update, where: { key: $key }) {
      ...CustomFieldDetails
    }
  }
`;
const DeleteCustomFieldMutation = gql`
  ${CustomFieldFragment}

  mutation DeleteCompanyCustomField($key: String!) {
    updateCompanyCustomField(update: { status: DELETED }, where: { key: $key }) {
      ...CustomFieldDetails
    }
  }
`;

export default {
  name: 'CompanyCustomFieldSettings',
  components: {
    LoadingSpinner,
    ErrorBlock,
    CustomFieldBlock,
  },
  data() {
    const visible = onboardingSteps.reduce(
      (out, { name }) => ({ ...out, [name]: false }),
      { registration: false }
    );
    return {
      company: null,
      fatalErr: null,
      newFields: [],
      visible
    };
  },
  metaInfo: {
    title: 'Custom Field Settings',
  },
  apollo: {
    company: {
      query: ReadCustomFieldsQuery,
      error(err) {
        this.fatalErr = `${err}`;
      },
    },
  },
  computed: {
    allowedSteps() {
      const { siteBranding: { onboarding } = {} } = this.company || {};
      let steps = (Array.isArray(onboarding) ? onboarding : []).reduce((out, { name }) => {
        const step = onboardingSteps.find(step => step.name === name);
        if (step) {
          out.push(step);
        }
        return out;
      }, []);

      const registrationGroupTypes = [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'JOB_SENIORITY', 'QUALIFICATIONS', 'DATE', 'JOB_HISTORY', 'LANGUAGES', 'JOB_TITLES', 'LOCATION', 'MULTI_SELECT' ];
      steps = [ { name: 'registration', displayName: 'Registration', types: registrationGroupTypes }, ...steps ];
      return steps.filter(step => ![ 'video', 'registernetwork' ].includes(step.name));
    },
  },
  methods: {
    addNewField(groupKey) {
      this.newFields.push({ groupKey, key: Math.random() });
      this.$scrollTo('.btn#add-new-field');
    },
    removeNewField(i) {
      this.newFields.splice(i, 1);
    },
    async createField(i, { key, ...create }) {
      console.log('createField', { create });

      try {
        await this.$apollo.mutate({
          mutation: CreateCustomFieldMutation,
          variables: {
            create: {
              ...create,
              status: 'ACTIVE',
            },
          },
          update(store, { data: { createCompanyCustomField: field } }) {
            const data = store.readQuery({ query: ReadCustomFieldsQuery });
            data.company.customFields.push(field);
            store.writeQuery({ query: ReadCustomFieldsQuery, data });
          },
        });

        if (create.group === 'jobs' && !this.company.customFields.find(({ key }) => key === 'EMPLOYEE-JOB-SELECTIONS')) {
          await this.$apollo.mutate({
            mutation: CreateCustomFieldMutation,
            variables: {
              create: {
                type: 'CUSTOM_FIELD_SET',
                name: 'Employee job selections',
                status: 'ACTIVE',
              },
            },
            update(store, { data: { createCompanyCustomField: field } }) {
              const data = store.readQuery({ query: ReadCustomFieldsQuery });
              data.company.customFields.push(field);
              store.writeQuery({ query: ReadCustomFieldsQuery, data });
            },
          });
        }

        this.removeNewField(i);
        this.$notification({
          title: 'Custom field created',
          message: 'Successfully saved the new custom field',
          variant: 'success',
        });
      } catch (err) {
        console.error(err);
        this.$notification({
          title: 'An error occurred saving the custom field',
          message: `${err}`,
          variant: 'danger',
        });
      }
    },
    async updateField(i, { key, type, ...update }) {
      try {
        await this.$apollo.mutate({
          mutation: UpdateCustomFieldMutation,
          variables: {
            key,
            update,
          },
          update(store, { data: { updateCompanyCustomField: field } }) {
            const data = store.readQuery({ query: ReadCustomFieldsQuery });
            data.company.customFields.splice(i, 1, field);
            store.writeQuery({ query: ReadCustomFieldsQuery, data });
          },
        });

        this.$notification({
          title: 'Custom field updated',
          message: 'Successfully updated the custom field',
          variant: 'success',
        });
      } catch (err) {
        console.error(err);
        this.$notification({
          title: 'An error occurred updating the custom field',
          message: `${err}`,
          variant: 'danger',
        });
      }
    },
    async removeField({ key }) {
      try {
        await this.$apollo.mutate({
          mutation: DeleteCustomFieldMutation,
          variables: {
            key,
          },
          update(store) {
            const data = store.readQuery({ query: ReadCustomFieldsQuery });
            const i = data.company.customFields.findIndex(el => el.key === key);
            data.company.customFields.splice(i, 1);
            store.writeQuery({ query: ReadCustomFieldsQuery, data });
          },
        });

        this.$notification({
          title: 'Custom field deleted',
          message: 'Successfully deleted the custom field',
          variant: 'success',
        });
      } catch (err) {
        console.error(err);
        this.$notification({
          title: 'An error occurred deleting the custom field',
          message: `${err}`,
          variant: 'danger',
        });
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/CustomFields.vue